import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Dialog from './Dialog';

// Page controls - filters, search, counts, action buttons, sub-page tabs - live
// in the top bar, not in the page. But pages render inside .content-area, which
// is a *sibling* of the header, so there is no prop path from a page up to the
// bar without threading state through every dashboard shell.
//
// So the bar publishes an empty slot element and pages portal into it. Nothing
// about a page's state moves: a page still owns its own filters, it just
// renders the controls somewhere else on screen. A page that renders no PageBar
// leaves the slot empty, and an empty slot has no height - which is how the
// control tier collapses on pages that have nothing to put in it.
//
// Below 1024px the bar is hidden, so PageBar falls back to rendering in normal
// flow at the top of the page (see useBarVisible).

const PageBarContext = createContext(null);

// Past this many sub-tabs they stop sharing tier 3 with the controls and take
// the row for themselves, pushing controls down to tier 4. Hiding navigation
// behind a "more" menu to save a row is the trade we decided against.
export const SUBTAB_SPLIT = 5;

/*
  **A list's Columns button is handed up here too**, the owner's decision of 3
  October 2026. He pointed at it and said: *"what is this and why is it in this
  location? looks weird by itself."* It sat on a transparent strip of its own
  above the table, and once the page went as wide as the bar earlier the same day
  it was stranded in the far corner on its own.

  **It cannot simply portal into the slot like a page's controls do**, because
  the slot already holds the whole bar and anything portalled in beside it lands
  outside the row the controls are on. So a list registers an opener here and the
  bar DRAWS the button, in among the search and the filters where it belongs.

  **A list only hands it up when a page bar is actually in the top bar.** Not
  every screen with an arrangeable table has one (the unverified matches list is
  drawn inside another page), and below 1024 the bar is not on screen at all. In
  both of those the list keeps its own strip, which below 1024 the stylesheet
  hides anyway, because a stacked row has no columns to arrange.
*/
export function PageBarProvider({ children }) {
  const [slot, setSlot] = useState(null);
  /* Keyed by the table's own layout key, so two lists on one page cannot
     overwrite each other, and a list that unmounts takes only its own. */
  const [tableControls, setTableControls] = useState({});
  const [barsInBar, setBarsInBar] = useState(0);

  const setTableControl = useCallback((key, opener) => {
    setTableControls(prev => {
      if (opener === null) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === opener) return prev;
      return { ...prev, [key]: opener };
    });
  }, []);

  const addBarInBar = useCallback(n => setBarsInBar(c => c + n), []);

  const value = useMemo(
    () => ({ slot, setSlot, tableControls, setTableControl, barInBar: barsInBar > 0, addBarInBar }),
    [slot, tableControls, setTableControl, barsInBar, addBarInBar],
  );
  return <PageBarContext.Provider value={value}>{children}</PageBarContext.Provider>;
}

export function usePageBarSlot() {
  return useContext(PageBarContext) || EMPTY_SLOT;
}
const EMPTY_SLOT = {
  slot: null, setSlot: () => {},
  tableControls: {}, setTableControl: () => {}, barInBar: false, addBarInBar: () => {},
};

// The one breakpoint that decides which layout a dashboard is in, exported so
// the shells split their trees on exactly the query this file tests. They have
// to agree: a tree that is off-screen but still mounted portals its controls
// into the visible bar, and two mounted trees rendering the same page put the
// same control band in there twice.
//
// The bar itself exists at every width - .dash-topbar just hides it below
// 1024px - so a slot can be registered while the bar is not on screen. Testing
// the breakpoint is the only reliable read of "is the bar actually visible",
// which is what keeps a page's controls from vanishing into a hidden bar.
export const DESKTOP_QUERY = '(min-width: 1024px)';

function useBarVisible() {
  const [visible, setVisible] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true),
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = e => setVisible(e.matches);
    // addListener is the Safari <14 spelling; still worth keeping for iPads.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    setVisible(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return visible;
}

export function SubTabs({ tabs, active, onChange }) {
  return (
    <span className="tb-subtabs">
      {tabs.map(t => (
        <button
          key={t.key}
          className={'tb-stab' + (t.key === active ? ' on' : '')}
          onClick={() => onChange && onChange(t.key)}
        >
          {t.label}
          {t.count !== undefined && t.count !== null && <span className="tb-stab-n">{t.count}</span>}
        </button>
      ))}
    </span>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search...', style, ...rest }) {
  return (
    <input
      className="tb-search"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={style}
      {...rest}
    />
  );
}

/**
 * title        page name, shown where the in-page title block used to be
 * subTabs      [{ key, label, count? }] - sub-page navigation
 * controls     filters and search, rendered after the title
 * meta         right-aligned status text, e.g. "128 accounts"
 * metaFullWidth below 1024, give meta a line of its own rather than letting it
 *               size to its content. For a strip of numbers that has to spread
 *               across the width, which is how Group C's approved drawings show
 *               the cycle's four counts at 700 and 390.
 * actions      right-aligned buttons, primary last
 * drawer       overflow filters; renders a toggle and an extra row when open
 * filterCount  badge on the toggle, so active filters are visible when shut
 * quiet        acts that fold behind a "..." button below 1024. See below.
 */
/*
  **The overflow, added 8 September 2026 while walking Group G's nine screens.**

  Three of its approved 390 drawings show the same shape: a search, a filter
  chip, a "..." and one primary button, on one row. The app had no "..." at all,
  so every act a screen handed up got a row of its own and Account Mappings'
  four took three rows before the list started.

  **The filters already had an answer and the acts did not.** `drawer` folds
  controls away; nothing folded acts. So a screen hands its quiet acts in as
  `quiet` and they sit inline on a desk, exactly as they did, and go behind a
  "..." below 1024. The primary act never folds: rule 33 says a primary act is
  always out, and rule 26 says it never falls off.

  **It opens a dialog rather than a menu**, which is the same call Group F made
  about its filters and for the same reason: a menu pinned to a button has
  nowhere to go on a phone and no room to say what each thing does.
*/
export default function PageBar({
  title,
  subtitle,
  quiet,
  subTabs,
  twoRows = false,
  activeSubTab,
  onSubTabChange,
  controls,
  meta,
  metaFullWidth = false,
  actions,
  drawer,
  filterCount = 0,
  filterLabel = 'Filters',
}) {
  const { slot, tableControls, barInBar, addBarInBar } = usePageBarSlot();
  const barVisible = useBarVisible();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  /*
    **Say whether this bar is really in the top bar.** A list reads this to
    decide whether to hand its Columns button up or keep its own strip, and
    there are two ways to not be in it: the page published no slot, or the
    screen is under 1024 and the bar is not drawn at all.
  */
  const inBar = !!slot && barVisible;
  useEffect(() => {
    if (!inBar) return undefined;
    addBarInBar(1);
    return () => addBarInBar(-1);
  }, [inBar, addBarInBar]);

  const tabs = subTabs || [];
  /*
    **Two bands rather than one.** A screen with a name, a strip of live numbers
    AND a row of controls does not fit one band, and Group C's approved drawing
    of Inventory Counts shows it as two: the cycle and its numbers on one line,
    the search, the filters and the two buttons on the next. Asked for by name
    rather than guessed at from what was passed, so every existing caller draws
    exactly as it did. Added 5 September 2026.
  */
  const split = twoRows || tabs.length > SUBTAB_SPLIT;

  const tabGroup = tabs.length > 0
    ? <SubTabs tabs={tabs} active={activeSubTab} onChange={onSubTabChange} />
    : null;

  const toggle = drawer ? (
    <button className="tb-filter-toggle" onClick={() => setDrawerOpen(o => !o)}>
      {filterLabel}
      {filterCount > 0 && <span className="tb-filter-n">{filterCount}</span>}
      {/* **A drawn chevron, not a typed triangle.** These were two symbol
          characters, which the brand guide's section 0 rule 12 rules out in a
          delivered file, and they are the only symbols OnHand's navigation put
          on screen. A character also draws at whatever size and weight the
          person's fallback face happens to give it; this is the same mark the
          rest of the app uses. */}
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true"
        stroke="var(--text-secondary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: drawerOpen ? 'rotate(180deg)' : 'none', transition: 'transform var(--oh-dur-fast) var(--oh-ease)' }}>
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>
  ) : null;

  // Subtitles sit inline beside the name rather than stacked under it - a
  // second line would put back the height the in-page title block just gave up.
  const titleBlock = title ? (
    <>
      <span className="tb-title">{title}</span>
      {subtitle && <span className="tb-subtitle">{subtitle}</span>}
    </>
  ) : null;

  // The controls half of the band: everything that isn't the page name or the
  // sub-tabs. Kept in one place so it reads the same whether it shares tier 3
  // or gets pushed down to tier 4.
  const more = quiet ? (
    <>
      <span className="tb-quiet-wide">{quiet}</span>
      <button className="tb-more" aria-label="More things you can do here"
        onClick={() => setMoreOpen(true)}>...</button>
    </>
  ) : null;

  /*
    **The Columns buttons the lists on this page handed up.** Drawn beside the
    filter toggle rather than with the search, because both shape what the table
    looks like rather than what it holds. A page with no arrangeable table hands
    up nothing and this is empty.
  */
  const tableButtons = barInBar
    ? Object.entries(tableControls).map(([key, c]) => (
      /* **The Filters chip's look, not the strip's.** In the bar it stands
         beside Filters and does the same kind of job, and a 30 pixel chip in a
         row of 32 pixel ones reads as a mistake. */
      <button key={key} type="button" className="tb-filter-toggle tb-table-cols" onClick={c.open}>
        {c.label || 'Columns'}
      </button>
    ))
    : null;
  const hasTableButtons = !!tableButtons && tableButtons.length > 0;

  const controlRow = (
    <>
      {controls}
      <span className="tb-grow" />
      {meta && <span className="tb-count">{meta}</span>}
      {meta && (toggle || more || actions || hasTableButtons) && <span className="tb-vr" />}
      {toggle}
      {tableButtons}
      {more}
      {actions}
    </>
  );

  /*
    **Where the live numbers sit is the difference between the two shapes.**
    With many sub-tabs the split exists to give the tabs a line of their own, and
    the numbers belong down with the controls. With `twoRows` the split exists
    because the numbers are the point: rule 5 puts them on their own strip beside
    the name of the thing they describe, and the controls get the second line.
  */
  const rows = twoRows ? (
    <>
      <div className="tb-band">
        {titleBlock}
        {/* **Sub-tabs belong on the first band when a page asks for two rows**,
            added 6 September 2026 for the Item Catalog. It has two tabs, not
            six, so it never reached the `split` shape below, and everything it
            hands up landed on one row: the tabs, the search, the vendor filter,
            the count, the drawer and three buttons. The search box sat on top of
            the second tab. Two tabs is not enough to earn a row of their own,
            and a page that also has a bar full of controls is. */}
        {title && tabGroup && <span className="tb-vr" />}
        {tabGroup}
        <span className="tb-grow" />
        {meta && <span className={'tb-count' + (metaFullWidth ? ' tb-count-full' : '')}>{meta}</span>}
      </div>
      <div className="tb-band">
        {controls}
        <span className="tb-grow" />
        {toggle}
        {tableButtons}
        {more}
        {actions}
      </div>
    </>
  ) : split ? (
    <>
      <div className="tb-band">
        {titleBlock}
        {title && tabGroup && <span className="tb-vr" />}
        {tabGroup}
        <span className="tb-grow" />
      </div>
      <div className="tb-band">{controlRow}</div>
    </>
  ) : (
    <div className="tb-band">
      {titleBlock}
      {title && (tabGroup || controls) && <span className="tb-vr" />}
      {tabGroup}
      {tabGroup && controls && <span className="tb-vr" />}
      {controlRow}
    </div>
  );

  const content = (
    <>
      {rows}
      {drawer && drawerOpen && <div className="tb-drawer">{drawer}</div>}
      {moreOpen && (
        <Dialog
          kind="info"
          icon={<IconMore />}
          title={title || 'This page'}
          says="Everything else you can do here."
          onClose={() => setMoreOpen(false)}
          footer={<button className="btn btn-quiet" onClick={() => setMoreOpen(false)}>Close</button>}
        >
          <div className="tb-more-list" onClick={() => setMoreOpen(false)}>{quiet}</div>
        </Dialog>
      )}
    </>
  );

  // No slot, or a viewport where the bar is hidden: render in place so the
  // controls are never lost.
  if (!slot || !barVisible) return <div className="tb-inline">{content}</div>;
  return createPortal(content, slot);
}

const IconMore = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="19" cy="12" r="1.9" />
  </svg>
);
